import { useRef, useState } from 'react'
import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Button,
    Text,
    useToast,
} from '@chakra-ui/react'

import { useSearchState } from '@hooks/useSearchState'

import { useMatchEventEditorData } from '@queries/useMatchEventEditorData'

import { matchEventSortFn } from './utils'

export function DeleteAllEventsButton() {
    const toast = useToast()
    const cancelRef = useRef(null)
    // Blocks a second confirm click before the mutation's pending state reaches the render
    const isConfirmingRef = useRef(false)
    const [isConfirmOpen, setIsConfirmOpen] = useState(false)
    const [, setSearchState] = useSearchState()

    const { events, removeAll, isRemovingAll, isRemoving } = useMatchEventEditorData()

    // Latest first, so no remaining event ever depends on one that was already deleted
    const removableEventIds = events
        .filter(event => event.isRemovable)
        .sort(matchEventSortFn)
        .map(event => event.id)

    const isBusy = isRemovingAll || isRemoving

    const handleClick = () => {
        if (removableEventIds.length === 0) {
            toast({
                position: 'top',
                title: 'No events to delete',
                description: 'This match has no events that can be removed.',
                status: 'info',
                duration: 5000,
                isClosable: true,
            })

            return
        }

        setIsConfirmOpen(true)
    }

    const handleConfirm = async () => {
        if (isConfirmingRef.current) {
            return
        }

        isConfirmingRef.current = true

        setIsConfirmOpen(false)

        try {
            await removeAll(removableEventIds)

            setSearchState({ eventId: undefined })
        } catch {
            // Errors are surfaced via toast in useMatchEventEditorData
        } finally {
            isConfirmingRef.current = false
        }
    }

    return (
        <>
            <Button
                colorScheme="red"
                variant="outline"
                size="sm"
                ml={3}
                onClick={handleClick}
                isDisabled={isBusy}
                isLoading={isRemovingAll}
                loadingText="Deleting…">
                Delete all events
            </Button>

            <AlertDialog isOpen={isConfirmOpen} leastDestructiveRef={cancelRef} onClose={() => setIsConfirmOpen(false)}>
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Delete all events
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            <Text>
                                Are you sure you want to delete <strong>{removableEventIds.length}</strong> event
                                {removableEventIds.length === 1 ? '' : 's'}? You cannot undo this action.
                            </Text>
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={() => setIsConfirmOpen(false)}>
                                Cancel
                            </Button>
                            <Button colorScheme="red" onClick={handleConfirm} isDisabled={isBusy} ml={3}>
                                Delete all
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </>
    )
}
